// Resolve the person a generated quote is addressed to from RFQ authority.
// CRM contacts are useful relationship context, but they must not replace an
// explicit "Requested by" / "Quote to" instruction on the current work order.

export interface QuoteAttentionResult {
    name: string;
    source: string;
}

export interface QuoteAttentionFormData {
    rfqContent?: string | null;
    rfqSummary?: string | null;
    notes?: string | null;
    clientEmail?: string | null;
    attentionTo?: string | null;
    attentionToSource?: string | null;
    clientName?: string | null;
    [key: string]: unknown;
}

const EMPTY_OR_GENERIC = /^(?:n\/?a|none|unknown|the owners?|owners corporation|body corporate|common property)$/i;

function plainText(value: unknown): string {
    return String(value || '')
        .replace(/<\/(?:p|div|li|tr|h[1-6])>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\r/g, '')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n\s+/g, '\n')
        .trim();
}

function cleanPerson(value: unknown): string {
    const cleaned = String(value || '')
        .replace(/\s*(?:,|\||\s+-\s+).*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned || EMPTY_OR_GENERIC.test(cleaned)) return '';
    if (!/^[A-Za-z][A-Za-z'’-]*(?:\s+[A-Za-z][A-Za-z'’-]*){1,4}$/.test(cleaned)) return '';
    return cleaned;
}

function personFromEmail(value: unknown): string {
    const email = String(value || '').trim().toLowerCase();
    const match = email.match(/^([a-z][a-z'-]+)[._-]([a-z][a-z'-]+)@/i);
    if (!match) return '';
    const title = (part: string) => part
        .split(/([-'])/)
        .map(token => /^[-']$/.test(token) ? token : token.charAt(0).toUpperCase() + token.slice(1))
        .join('');
    return `${title(match[1]!)} ${title(match[2]!)}`;
}

export function extractRfqAttention(formData: QuoteAttentionFormData = {}): QuoteAttentionResult {
    const sources: Array<[string, unknown]> = [
        ['rfqContent', formData.rfqContent],
        ['rfqSummary', formData.rfqSummary],
        ['notes', formData.notes],
    ];
    const patterns = [
        /(?:^|\n)\s*requested\s+by\s*:?[ \t]*(?:\n\s*)?([^\n]{3,100})/i,
        /(?:^|\n)\s*(?:quote|quotation)\s+requested\s+by\s*:?[ \t]*(?:\n\s*)?([^\n]{3,100})/i,
        /(?:^|\n)\s*(?:w\.?\s*o\.?|work\s+order)\s+issued\s+by\s*:?[ \t]*(?:\n\s*)?([^\n]{3,100})/i,
    ];
    for (const [source, raw] of sources) {
        const text = plainText(raw);
        if (!text) continue;
        for (const pattern of patterns) {
            const name = cleanPerson(text.match(pattern)?.[1]);
            if (name) return { name, source: `rfq-${source}` };
        }
    }
    const emailName = personFromEmail(formData.clientEmail);
    return emailName ? { name: emailName, source: 'rfq-client-email' } : { name: '', source: '' };
}

export function resolveQuoteAttention(formData: QuoteAttentionFormData = {}): QuoteAttentionResult {
    const existing = cleanPerson(formData.attentionTo);
    if (existing && formData.attentionToSource === 'manual') {
        return { name: existing, source: 'manual' };
    }
    const rfq = extractRfqAttention(formData);
    if (rfq.name) return rfq;
    return existing
        ? { name: existing, source: formData.attentionToSource || 'existing' }
        : { name: cleanPerson(formData.clientName), source: 'client-name-fallback' };
}
